import { registerPlugin, type PluginListenerHandle } from '@capacitor/core';

/**
 * Native accelerometer plugin (joltstream-owned, lives in
 * `web/android-overlay/`). Bridges Android's SensorManager TYPE_ACCELEROMETER
 * at SENSOR_DELAY_GAME (~50 Hz) into JS via Capacitor's notifyListeners.
 *
 * The web-side plugin call (`JoltAccel.start()` etc.) falls through to a
 * no-op stub when no native bridge is registered — i.e. when running in
 * a normal browser. Callers should only construct a NativeAccelSource
 * when detectRuntime() === 'capacitor'.
 *
 * Why this exists: the default `BrowserAccelSource` listens to
 * `window.devicemotion`, which Chromium throttles aggressively inside a
 * wrapped WebView (delivering <10 Hz unevenly even when the same browser
 * gives 50 Hz on the standalone PWA). See rhodium-org/joltstream#2.
 */
export interface NativeAccelEvent {
  /** Acceleration along the device X axis (m/s², gravity included). */
  x: number;
  /** Acceleration along the device Y axis (m/s², gravity included). */
  y: number;
  /** Acceleration along the device Z axis (m/s², gravity included). */
  z: number;
  /** Wall-clock timestamp at delivery (ms since epoch). */
  ts: number;
}

export interface JoltAccelPlugin {
  start(): Promise<void>;
  stop(): Promise<void>;
  addListener(
    eventName: 'accel',
    listenerFunc: (event: NativeAccelEvent) => void,
  ): Promise<PluginListenerHandle>;
}

export const JoltAccel = registerPlugin<JoltAccelPlugin>('JoltAccel');
